import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { NavLink, useLocation } from "react-router-dom";
import {
    LayoutDashboard,
    ChevronDown,
    Scale,
    Briefcase,
    Wallet,
    Users,
    LogOut,
    Search,
    Images,
    FileSignature,
    BellRing,
    ShieldCheck,
    Gavel,
    FolderKanban,
    FileText,
    Hammer,
    TrendingDown,
    FileEdit,
    FileCheck,
    ClipboardList,
    Receipt,
    Tag,
    Handshake,
    Building2,
    Wrench,
    CalendarDays,
    NotebookPen,
    Calendar,
    Bot,
    ScanFace,
    FileScan,
    Mail,
    MessageSquare,
    Sunrise,
    Sparkles,
    Clock,
    Star,
    RotateCcw,
    Truck,
    Car,
    AlertTriangle,
    Headset,
    Calculator,
    Settings2,
    MessageCircle,
    Send,
    LayoutGrid,
    Globe,
    Brain,
    Radar,
    Eye,
    EyeOff,
    CheckCircle2,
    Activity,
    BadgePercent,
    History,
} from "lucide-react";
import { SIDEBAR_LAYOUT, getEntity } from "../lib/entities";
import { YENI_SIDEBAR } from "../lib/yeniSidebar";
import { AI_SIDEBAR } from "../lib/aiSidebar";
import {
    DEFAULT_FAVORITES,
    loadFavorites,
    saveFavorites,
    toggleFavorite,
    isFavorite,
} from "../lib/sidebarFavorites";
import { isMenuPathVisible, loadHiddenPaths } from "../lib/sidebarMenuPrefs";
import {
    loadMenuLayout,
    syncLayoutWithCatalog,
} from "../lib/sidebarMenuLayout";
import { isYeniMenuPathAllowed } from "../lib/sidebarYeniAccess";
import { useAuth } from "../lib/auth";
import { usePermissions } from "../lib/usePermissions";
import LiveNotificationCenter from "./LiveNotificationCenter";
import WhatsAppNotificationCenter from "./WhatsAppNotificationCenter";
import { UserProfileMenu } from "./UserProfileMenu";
import SidebarMenuCustomizer from "./SidebarMenuCustomizer";

const ICONS = {
    Images,
    FileSignature,
    BellRing,
    ShieldCheck,
    Scale,
    Gavel,
    FolderKanban,
    FileText,
    Hammer,
    TrendingDown,
    FileEdit,
    FileCheck,
    ClipboardList,
    Wallet,
    Receipt,
    Tag,
    Users,
    Handshake,
    Building2,
    Wrench,
    CalendarDays,
    NotebookPen,
    Bot,
    ScanFace,
    FileScan,
    Mail,
    MessageSquare,
    Sunrise,
    Clock,
    LayoutDashboard,
    Calendar,
    Sparkles,
    Truck,
    Car,
    AlertTriangle,
    Headset,
    Calculator,
    Settings2,
    MessageCircle,
    Send,
    LayoutGrid,
    FolderKanban,
    Globe,
    Brain,
    Radar,
    BadgePercent,
    History,
};

const CAT_ICON = {
    Yönetim: Briefcase,
    CRM: Users,
    Filo: Truck,
    Hukuk: Scale,
    Finans: Wallet,
    Sistem: Wrench,
    "Yapay Zeka": Brain,
    Yeni: Sparkles,
    genel: LayoutDashboard,
    ai: Brain,
    yeni: Sparkles,
};

function groupNavIcon(group) {
    return CAT_ICON[group.key] || CAT_ICON[group.label] || (group.custom ? FolderKanban : Briefcase);
}

const NAV_LINK_CLASS = ({ isActive }) =>
    `group relative flex items-center gap-3 px-3 py-2 rounded-lg text-xs font-medium transition-all duration-200 ${
        isActive
            ? "bg-gradient-to-r from-[#D4AF37]/20 to-transparent text-[#D4AF37] border-l-2 border-[#D4AF37] pl-2.5 shadow-[inset_4px_0_12px_rgba(212,175,55,0.08)]"
            : "text-slate-400 hover:text-white hover:bg-white/[0.03] pl-3"
    }`;

function SidebarNavRow({
    to,
    title,
    icon: IconName,
    active,
    onNavigate,
    testId,
    favorites,
    onToggleFavorite,
    end,
}) {
    const Icn = ICONS[IconName] || FileText;
    const pinned = isFavorite(favorites, to);

    return (
        <div className="relative group">
            <NavLink
                to={to}
                end={end}
                onClick={onNavigate}
                data-testid={testId}
                className={({ isActive }) =>
                    NAV_LINK_CLASS({ isActive: isActive || active })
                }
            >
                <Icn className={`h-4 w-4 shrink-0 transition-transform duration-200 group-hover:scale-110 ${active ? "text-[#D4AF37]" : "opacity-70 group-hover:opacity-100"}`} />
                <span className="truncate flex-1 pr-6 tracking-wide">{title}</span>
            </NavLink>
            <button
                type="button"
                title={pinned ? "Sık kullanılanlardan çıkar" : "Sık kullanılanlara ekle"}
                aria-label={pinned ? "Sık kullanılanlardan çıkar" : "Sık kullanılanlara ekle"}
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onToggleFavorite(to);
                }}
                className={`ap-sidebar-pin absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 rounded flex items-center justify-center text-slate-500 hover:text-[#D4AF37] hover:bg-white/5 opacity-0 group-hover:opacity-100 transition-all duration-200 ${
                    pinned ? "is-pinned !opacity-100 text-[#D4AF37]" : ""
                }`}
            >
                <Star
                    className={`h-3 w-3 ${pinned ? "fill-[#D4AF37] text-[#D4AF37]" : ""}`}
                />
            </button>
        </div>
    );
}

export default function Sidebar({ onNavigate }) {
    const location = useLocation();
    const { signOut } = useAuth();
    const { can } = usePermissions();
    const [query, setQuery] = useState("");
    const [favorites, setFavorites] = useState(() => loadFavorites());
    const [hiddenPaths, setHiddenPaths] = useState(() => loadHiddenPaths());
    const [menuCustomizeOpen, setMenuCustomizeOpen] = useState(false);
    const [favOpen, setFavOpen] = useState(true);
    const [open, setOpen] = useState({});
    const [smartMode, setSmartMode] = useState(false);

    const handleToggleFavorite = useCallback((path) => {
        setFavorites((prev) => toggleFavorite(prev, path));
    }, []);

    const resetFavorites = useCallback(() => {
        const next = [...DEFAULT_FAVORITES];
        saveFavorites(next);
        setFavorites(next);
    }, []);

    const menuVisible = useCallback(
        (path) => isMenuPathVisible(path, hiddenPaths),
        [hiddenPaths]
    );

    // Akıllı Sidebar Modu için kullanılmayan/pasif modülleri filtrele
    const isModuleUsed = useCallback((path) => {
        if (!smartMode) return true;
        
        // Sık kullanılanlar her zaman gösterilir
        if (favorites.includes(path)) return true;

        // Akıllı filtre: MBS Hukuk bürosunun aktif olarak kullandığı ana operasyonel modüller
        const activeOperationalPaths = [
            "/",
            "/takvim",
            "/ai/merkez",
            "/ai/sohbet",
            "/yeni/tahkim-merkezi",
            "/yeni/dosya-operasyon-merkezi",
            "/yeni/iletisim-merkezi",
            "/yeni/rayic-parca-merkezi",
            "/yeni/deger-kaybi-merkezi",
            "/yeni/finans-merkezi",
            "/yeni/kusur-sorgulama",
            "/yeni/aktuerya-merkezi",
            "/yeni/icra-merkezi",
            "/yeni/araci-merkezi",
            "/yeni/filo-merkezi",
            "/yeni/site-yonetimi",
            "/yeni/sistem-gunlugu",
            "/e/audit-loglari",
            "/e/hesap-hareketleri",
            "/e/sigorta-dosyalari",
            "/e/muvekkiller",
            "/e/aracilar",
            "/e/deger-kaybi",
            "/e/tahkim-dosyalari",
            "/e/evraklar",
        ];

        return activeOperationalPaths.some(p => p === path || path.startsWith(p + "/"));
    }, [smartMode, favorites]);

    /** Arama varken gizli modüller de listelenir (yine de ulaşılabilir). */
    const showInSidebar = useCallback(
        (path) => (Boolean(query) || menuVisible(path)) && isModuleUsed(path),
        [query, menuVisible, isModuleUsed]
    );

    const resolved = useMemo(() => {
        return SIDEBAR_LAYOUT.map((cat) => ({
            key: cat.key,
            label: cat.key,
            items: cat.items
                .map((it) => {
                    if (it.path) {
                        return {
                            key: it.path,
                            navKey: `${cat.key}-${it.path}`,
                            title: it.title,
                            label: it.title,
                            icon: it.icon || "FileText",
                            resource: null,
                            path: it.path,
                        };
                    }
                    const ent = getEntity(it.key);
                    if (!ent) return null;
                    return {
                        key: it.key,
                        navKey: `${cat.key}-${it.key}`,
                        title: it.title || ent.title,
                        label: ent.label,
                        icon: it.icon || ent.icon,
                        resource: ent.resource,
                        path: `/e/${it.key}`,
                    };
                })
                .filter(Boolean),
        }));
    }, []);

    const navCatalog = useMemo(() => {
        const map = new Map();
        const add = (path, title, icon) => {
            if (!path || map.has(path)) return;
            map.set(path, { path, title, icon });
        };

        add("/", "Gösterge Paneli", "LayoutDashboard");
        add("/takvim", "Takvim", "Calendar");

        AI_SIDEBAR.items.forEach((it) => add(it.path, it.title, it.icon));
        YENI_SIDEBAR.items.forEach((it) => add(it.path, it.title, it.icon));

        resolved.forEach((cat) => {
            cat.items.forEach((e) => add(e.path, e.title, e.icon));
        });

        return map;
    }, [resolved]);

    const favoriteItems = useMemo(() => {
        return favorites
            .map((path) => navCatalog.get(path))
            .filter(Boolean)
            .filter((item) => showInSidebar(item.path))
            .filter((item) => {
                if (!query) return true;
                const q = query.toLowerCase();
                return (
                    item.title.toLowerCase().includes(q) ||
                    item.path.toLowerCase().includes(q)
                );
            });
    }, [favorites, navCatalog, query, showInSidebar]);

    const defaultCatalogGroups = useMemo(() => {
        const toItem = (path, title) => ({ path, title });
        const groups = [
            {
                key: "genel",
                label: "Genel",
                items: [
                    toItem("/", "Gösterge Paneli"),
                    toItem("/takvim", "Takvim"),
                ],
            },
            {
                key: "ai",
                label: AI_SIDEBAR.key,
                items: AI_SIDEBAR.items.map((it) => toItem(it.path, it.title)),
            },
            {
                key: "yeni",
                label: YENI_SIDEBAR.key,
                items: YENI_SIDEBAR.items.map((it) => toItem(it.path, it.title)),
            },
        ];
        resolved.forEach((cat) => {
            groups.push({
                key: cat.key,
                label: cat.label,
                items: cat.items.map((e) => toItem(e.path, e.title)),
            });
        });
        return groups;
    }, [resolved]);

    const [menuLayout, setMenuLayout] = useState(() =>
        loadMenuLayout(defaultCatalogGroups)
    );

    useEffect(() => {
        setMenuLayout((prev) => syncLayoutWithCatalog(prev, defaultCatalogGroups));
    }, [defaultCatalogGroups]);

    useEffect(() => {
        setOpen((prev) => {
            const next = { ...prev };
            for (const g of menuLayout.groups || []) {
                if (next[g.id] === undefined) {
                    next[g.id] = g.id === "ai" || g.id === "yeni" || g.id === "Yönetim";
                }
            }
            return next;
        });
    }, [menuLayout.groups]);

    const navItemByPath = useMemo(() => {
        const m = new Map();
        const put = (item) => {
            if (item?.path) m.set(item.path, item);
        };
        put({
            path: "/",
            title: "Gösterge Paneli",
            icon: "LayoutDashboard",
            navKey: "home",
            label: "Panel",
        });
        put({
            path: "/takvim",
            title: "Takvim",
            icon: "Calendar",
            navKey: "takvim",
            label: "Takvim",
        });
        AI_SIDEBAR.items.forEach((it) =>
            put({
                path: it.path,
                title: it.title,
                icon: it.icon,
                navKey: `ai-${it.path}`,
                label: it.title,
            })
        );
        YENI_SIDEBAR.items.forEach((it) =>
            put({
                path: it.path,
                title: it.title,
                icon: it.icon,
                navKey: `yeni-${it.path}`,
                label: it.title,
            })
        );
        resolved.forEach((cat) => cat.items.forEach((e) => put(e)));
        return m;
    }, [resolved]);

    const filterFn = useCallback(
        (e) =>
            !query ||
            (e.label || "").toLowerCase().includes(query.toLowerCase()) ||
            (e.title || "").toLowerCase().includes(query.toLowerCase()) ||
            (e.resource || "").toLowerCase().includes(query.toLowerCase()),
        [query]
    );

    const sidebarNavGroups = useMemo(() => {
        return (menuLayout.groups || [])
            .map((g) => ({
                key: g.id,
                label: g.label,
                custom: Boolean(g.custom),
                items: (menuLayout.items[g.id] || [])
                    .map((path) => navItemByPath.get(path))
                    .filter(Boolean)
                    .filter((item) => isYeniMenuPathAllowed(item.path, can))
                    .filter(filterFn)
                    .filter((e) => showInSidebar(e.path)),
            }))
            .filter((g) => g.items.length > 0 || query);
    }, [menuLayout, navItemByPath, can, query, showInSidebar, filterFn]);

    const hiddenMenuCount = hiddenPaths.size;

    return (
        <aside
            data-testid="app-sidebar"
            className="fixed lg:sticky top-0 left-0 z-[260] lg:z-40 h-screen w-72 shrink-0 border-r border-[rgba(212,175,55,0.08)] bg-gradient-to-b from-[#0a1220] via-[#080e1a] to-[#050a14] flex flex-col shadow-[4px_0_24px_rgba(0,0,0,0.4)]"
        >
            {/* Brand — Lüks Altın & Lacivert */}
            <div className="px-6 pt-6 pb-5 border-b border-white/[0.03] bg-gradient-to-r from-[#D4AF37]/5 to-transparent relative overflow-hidden">
                <div className="absolute top-0 left-0 w-1 h-full bg-[#D4AF37]" />
                <div className="flex items-center gap-3">
                    <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-[#0e1a2b] to-[#050a14] border border-[#D4AF37]/30 flex items-center justify-center shadow-[0_0_15px_rgba(212,175,55,0.15)] overflow-hidden transition-transform duration-300 hover:scale-105">
                        <img
                            src="/static/brand/mbs-logo.png"
                            alt="MBS Hukuk"
                            className="h-8.5 w-auto max-w-[2.2rem] object-contain"
                            onError={(e) => {
                                e.currentTarget.src = "/static/img/mbs-logo.png";
                                e.currentTarget.onerror = () => {
                                    e.currentTarget.style.display = "none";
                                    e.currentTarget.nextElementSibling.style.display = "flex";
                                };
                            }}
                        />
                        <div
                            className="hidden h-full w-full bg-gradient-to-br from-[#8b1f2f] to-[#c9a961] items-center justify-center"
                            aria-hidden
                        >
                            <Scale className="h-5 w-5 text-white" strokeWidth={2.2} />
                        </div>
                    </div>
                    <div className="leading-tight">
                        <div className="font-display text-lg font-bold tracking-wider text-white flex items-center gap-1">
                            MBS Hukuk
                            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
                        </div>
                        <div className="text-[9px] uppercase tracking-[0.25em] text-[#D4AF37]/80 font-semibold mt-0.5">
                            Akıllı Sistem
                        </div>
                    </div>
                </div>
            </div>

            {/* Arama ve Akıllı Filtre Kontrolleri */}
            <div className="px-4 pt-4 space-y-2">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                    <input
                        data-testid="sidebar-search-input"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Modül veya işlem ara..."
                        className="w-full bg-black/30 border border-white/[0.06] rounded-lg pl-9 pr-3 py-2 text-xs text-white placeholder:text-slate-600 focus:border-[#D4AF37]/50 focus:ring-1 focus:ring-[#D4AF37]/20 outline-none transition-all duration-300"
                    />
                </div>
                
                <div className="flex gap-1.5">
                    {/* Akıllı Sidebar Modu Butonu */}
                    <button
                        type="button"
                        onClick={() => {
                            setSmartMode(!smartMode);
                            toast.success(smartMode ? "Tüm modüller gösteriliyor" : "Akıllı görünüm aktif: Kullanılmayan modüller gizlendi!");
                        }}
                        className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 px-2.5 rounded-md text-[10px] font-medium border transition-all duration-200 ${
                            smartMode 
                                ? "bg-[#D4AF37]/10 border-[#D4AF37]/30 text-[#D4AF37]" 
                                : "bg-black/20 border-white/[0.05] text-slate-400 hover:text-slate-200"
                        }`}
                        title={smartMode ? "Akıllı Sidebar Aktif (Kullanılmayanlar Gizli)" : "Akıllı Sidebar Pasif (Tüm Modüller Açık)"}
                    >
                        <Activity className="h-3 w-3" />
                        {smartMode ? "Akıllı Görünüm" : "Tüm Modüller"}
                    </button>

                    {/* Menü Özelleştir */}
                    <button
                        type="button"
                        data-testid="sidebar-menu-customize"
                        onClick={() => setMenuCustomizeOpen(true)}
                        className="flex items-center justify-center gap-1.5 py-1.5 px-2.5 rounded-md text-[10px] font-medium bg-black/20 border border-white/[0.05] text-slate-400 hover:text-slate-200 hover:border-white/10 transition-all duration-200"
                    >
                        <Settings2 className="h-3 w-3" />
                        Düzenle
                    </button>
                </div>
            </div>

            <SidebarMenuCustomizer
                open={menuCustomizeOpen}
                onOpenChange={setMenuCustomizeOpen}
                layout={menuLayout}
                onLayoutChange={setMenuLayout}
                catalogGroups={defaultCatalogGroups}
                navCatalog={navCatalog}
                hiddenPaths={hiddenPaths}
                onHiddenChange={setHiddenPaths}
            />

            <nav className="mt-4 flex-1 overflow-y-auto px-3 pb-4 space-y-3 custom-scrollbar">
                {/* Sık kullanılanlar */}
                {favoriteItems.length > 0 && (
                    <div className="ap-sidebar-fav bg-white/[0.01] border border-white/[0.03] rounded-xl p-2">
                        <div className="flex items-center justify-between px-1.5 pb-2 border-b border-white/[0.03] mb-1.5">
                            <button
                                type="button"
                                data-testid="nav-favorites-toggle"
                                onClick={() => setFavOpen((v) => !v)}
                                className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400 hover:text-[#D4AF37] transition-colors duration-200"
                            >
                                <Star className="h-3.5 w-3.5 fill-[#D4AF37] text-[#D4AF37]" />
                                Sık Kullanılanlar
                                <ChevronDown
                                    className={`h-3 w-3 transition-transform duration-200 ${
                                        favOpen ? "" : "-rotate-90"
                                    }`}
                                />
                            </button>
                            <button
                                type="button"
                                data-testid="nav-favorites-reset"
                                onClick={resetFavorites}
                                className="text-[9px] text-slate-500 hover:text-rose-400 flex items-center gap-0.5 transition-colors"
                                title="Sıfırla"
                            >
                                <RotateCcw className="h-2.5 w-2.5" />
                                Sıfırla
                            </button>
                        </div>
                        {favOpen && (
                            <div className="space-y-0.5">
                                {favoriteItems.map((item) => {
                                    const active =
                                        location.pathname === item.path ||
                                        (item.path !== "/" &&
                                            location.pathname.startsWith(item.path + "/"));
                                    return (
                                        <SidebarNavRow
                                            key={`fav-${item.path}`}
                                            to={item.path}
                                            end={item.path === "/"}
                                            title={item.title}
                                            icon={item.icon}
                                            active={active}
                                            onNavigate={onNavigate}
                                            testId={`nav-fav-${item.path.replace(/\//g, "-") || "home"}`}
                                            favorites={favorites}
                                            onToggleFavorite={handleToggleFavorite}
                                        />
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}

                {/* Modül Grupları */}
                {sidebarNavGroups.map((cat) => {
                    const Icon = groupNavIcon(cat);
                    const isOpen = open[cat.key] !== false;
                    return (
                        <div key={cat.key} className="space-y-1">
                            <button
                                data-testid={`nav-cat-${cat.key.toLowerCase()}`}
                                onClick={() =>
                                    setOpen((s) => ({ ...s, [cat.key]: !isOpen }))
                                }
                                className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-[10px] font-bold uppercase tracking-widest transition-all duration-200 ${
                                    isOpen 
                                        ? "text-slate-300 bg-white/[0.02]" 
                                        : "text-slate-500 hover:text-slate-300 hover:bg-white/[0.01]"
                                }`}
                            >
                                <span className="flex items-center gap-2 min-w-0">
                                    <Icon className={`h-3.5 w-3.5 shrink-0 ${isOpen ? "text-[#D4AF37]" : "text-slate-500"}`} />
                                    <span className="truncate">{cat.label}</span>
                                </span>
                                <ChevronDown
                                    className={`h-3 w-3 shrink-0 transition-transform duration-200 ${
                                        isOpen ? "" : "-rotate-90"
                                    }`}
                                />
                            </button>
                            {isOpen && (
                                <div className="pl-1.5 space-y-0.5 border-l border-white/[0.02] ml-4 mt-1 transition-all duration-300">
                                    {cat.items.map((e) => {
                                        const active =
                                            location.pathname === e.path ||
                                            (e.path !== "/" &&
                                                location.pathname.startsWith(e.path + "/"));
                                        return (
                                            <SidebarNavRow
                                                key={`${cat.key}-${e.path}`}
                                                to={e.path}
                                                end={e.path === "/"}
                                                title={e.title}
                                                icon={e.icon}
                                                active={active}
                                                onNavigate={onNavigate}
                                                testId={`nav-${e.navKey || e.path.replace(/\//g, "-")}`}
                                                favorites={favorites}
                                                onToggleFavorite={handleToggleFavorite}
                                            />
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    );
                })}
            </nav>

            {/* Footer — Kullanıcı Profili */}
            <div className="border-t border-white/[0.03] px-4 py-3.5 flex items-center justify-between bg-black/20">
                <UserProfileMenu variant="sidebar" />
                <div className="flex items-center gap-1 lg:hidden">
                    <WhatsAppNotificationCenter />
                    <LiveNotificationCenter />
                    <button
                        data-testid="sidebar-logout-button"
                        onClick={signOut}
                        title="Çıkış Yap"
                        className="h-8 w-8 rounded-lg hover:bg-rose-500/10 flex items-center justify-center text-slate-400 hover:text-rose-400 transition-colors duration-200"
                    >
                        <LogOut className="h-4 w-4" />
                    </button>
                </div>
            </div>
        </aside>
    );
}
